/**
 * One thing a person filters by, as they think of it. A dimension can span several form
 * fields: a date range is one dimension with a start and an end field.
 */
export type FilterDimension<TValues> = {
  id: string;
  label: string;
  fields: ReadonlyArray<keyof TValues & string>;
  /** Short text for the active value, for example "Manuals". Return null to show the label only. */
  summary?: (values: TValues) => string | null;
  /** False for settings like sort order that change the list but are not a filter. */
  countsAsFilter?: boolean;
};

const valuesMatch = (first: unknown, second: unknown) =>
  JSON.stringify(first) === JSON.stringify(second);

export const isDimensionActive = <TValues>(
  dimension: FilterDimension<TValues>,
  values: TValues,
  defaultValues: TValues,
) => dimension.fields.some((field) => !valuesMatch(values[field], defaultValues[field]));

export const getActiveFilterDimensions = <TValues>(
  dimensions: ReadonlyArray<FilterDimension<TValues>>,
  values: TValues,
  defaultValues: TValues,
) =>
  dimensions.filter(
    (dimension) =>
      dimension.countsAsFilter !== false && isDimensionActive(dimension, values, defaultValues),
  );

/** One line per active filter, for example ["Tag: Manuals", "Description: pump"]. */
export const summarizeActiveFilters = <TValues>(
  dimensions: ReadonlyArray<FilterDimension<TValues>>,
  values: TValues,
  defaultValues: TValues,
) =>
  getActiveFilterDimensions(dimensions, values, defaultValues).map((dimension) => {
    const summary = dimension.summary?.(values);
    return summary ? `${dimension.label}: ${summary}` : dimension.label;
  });
